import React, { useState, useEffect, useRef } from "react";
import { message, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getReportJobs, deleteReportJob, markJobsSeen, downloadExportReport } from "Explore/Services/Report";
import { setUnseenExportCount } from "Redux/Slices/Common";
import ExportToolbar from "./Components/ExportToolbar";
import ExportList from "./Components/ExportList";
import ExportDetailDrawer from "./Components/ExportDetailDrawer";
import LibrarySidebar from "./Components/LibrarySidebar/LibrarySidebar";
import FavoritesPanel from "./Components/FavoritesPanel/FavoritesPanel";
import SavedSearchResultsPanel from "./Components/SavedSearchResultsPanel/SavedSearchResultsPanel";

import { useSearchParams } from "react-router-dom";
import { PanelLeft, Bot } from "lucide-react";
import { cn } from "lib/utils";
import { Badge } from "components/ui/badge";

const REPORT_TABS = [
  { key: "collaboration", label: "Collaboration" },
  { key: "benchmarking", label: "Benchmarking" },
  { key: "ra-reports", label: "RA Reports", badge: "Soon" },
];

const MyLibraryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeLibraryTab = searchParams.get("tab") || "fav-researchers";
  const [librarySidebarCollapsed, setLibrarySidebarCollapsed] = useState(false);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [downloadingId, setDownloadingId] = useState(null);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const dispatch = useDispatch();
  const pollIntervalRef = useRef(null);
  const deletedIdsRef = useRef(new Set());
  const initialSeenMarkedRef = useRef(false);
  const user = useSelector((state) => state.user?.data);
  const currentUserId = user?.id || user?._id || user?.username || "current_user";

  const isFavoriteTab = activeLibraryTab.startsWith("fav-");
  const isSavedSearchTab = activeLibraryTab.startsWith("saved-");
  const isReportGroupTab = !isFavoriteTab && !isSavedSearchTab;
  const currentReportTab = (activeLibraryTab === "reports" || !activeLibraryTab) ? "collaboration" : activeLibraryTab;
  const isRAReportTab = currentReportTab === "ra-reports";

  const fetchJobs = async () => {
    try {
      const jobs = await getReportJobs(currentUserId);
      const jobsArray = Array.isArray(jobs) ? jobs : (jobs.items || []);
      const formatted = jobsArray
        .map((j) => {
          const rawDate = j.created_at || j.createdAt;
          const createdAtStr = rawDate
            ? String(rawDate).includes("Z") || String(rawDate).includes("+")
              ? String(rawDate)
              : `${rawDate}Z`
            : null;

          const isAnalysis = j.module === "analysis" || (j.title && j.title.toLowerCase().includes("benchmark"));
          const source = isAnalysis ? "benchmarking" : "collaboration";

          let entityDisplay = "Unknown Entity";
          if (j.entity_name) {
            entityDisplay = j.entity_name;
          } else if (Array.isArray(j.entity_ids) && j.entity_ids.length > 0) {
            entityDisplay = j.entity_ids.length === 1 ? j.entity_ids[0] : `${j.entity_ids.length} entities`;
          } else if (isAnalysis) {
            entityDisplay = "Top 5 (Automated)";
          } else if (j.entity_id) {
            entityDisplay = j.entity_id;
          }

          return {
            id: j.id,
            title: j.title,
            source,
            module: j.module || (isAnalysis ? "analysis" : "collaboration"),
            createdAt: createdAtStr,
            entity: entityDisplay,
            status: j.status === "ready" ? "completed" : j.status,
            filters: j.filters,
            sections: j.sections,
            visual_options: j.visual_options,
            entity_id: j.entity_id,
            entity_ids: j.entity_ids,
            entity_type: j.entity_type,
            entity_name: j.entity_name,
            export_format: j.export_format || "pdf",
            is_seen: j.is_seen,
            error_message: j.error_message,
            metrics: j.metrics,
          };
        })
        .filter((j) => !deletedIdsRef.current.has(j.id));

      setReports(formatted);
      const unseenIds = formatted.filter((j) => j.is_seen === false).map((j) => j.id);
      if (unseenIds.length > 0 && !initialSeenMarkedRef.current) {
        initialSeenMarkedRef.current = true;
        markJobsSeen(unseenIds).catch(console.error);
      }
      dispatch(setUnseenExportCount(0));
      return formatted;
    } catch (e) {
      console.error("Failed to fetch jobs", e);
      message.error("Failed to load exports.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isReportGroupTab) {
      fetchJobs();
      pollIntervalRef.current = setInterval(async () => {
        const currentJobs = await fetchJobs();
        const hasPending = currentJobs.some(
          (j) => j.status === "pending" || j.status === "processing" || j.status === "queued"
        );
        if (!hasPending && pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }, 5000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isReportGroupTab]);

  const handleDelete = async (id) => {
    try {
      deletedIdsRef.current.add(id);
      await deleteReportJob(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      message.success("Export deleted successfully.");
    } catch (e) {
      deletedIdsRef.current.delete(id);
      console.error("Failed to delete export:", e);
      let errorDetail = "Failed to delete export.";
      const responseData = e.response?.data || e.data || e;
      if (responseData) {
        if (typeof responseData === "string") {
          errorDetail = responseData;
        } else if (responseData.detail) {
          errorDetail =
            typeof responseData.detail === "string"
              ? responseData.detail
              : JSON.stringify(responseData.detail);
        } else if (responseData.message) {
          errorDetail = responseData.message;
        } else if (e.message) {
          errorDetail = e.message;
        }
      } else if (e.message) {
        errorDetail = e.message;
      }
      Modal.error({
        title: "Cannot Delete Export",
        content: errorDetail,
        centered: true,
        zIndex: 99999,
      });
    }
  };

  const handleDownload = async (record) => {
    if (downloadingId) return;
    setDownloadingId(record.id);
    message.loading({ content: `Downloading ${record.title}...`, key: "export_downloading", duration: 0 });
    try {
      const ext = record.export_format === "xlsx" ? "xlsx" : "pdf";
      const fallbackName = `${record.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.${ext}`;
      await downloadExportReport(record.id, fallbackName);
      message.destroy("export_downloading");
      message.success("Download started.");
    } catch (e) {
      console.error("Failed to download export report:", e);
      message.destroy("export_downloading");
      message.error(e.message || "Failed to download export file.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  const filteredReports = reports.filter((report) => {
    const matchesSearch = report.title.toLowerCase().includes(searchText.toLowerCase());
    const matchesSource = report.source === currentReportTab;
    const matchesStatus =
      statusFilter === "all" ||
      report.status === statusFilter ||
      (statusFilter === "ready" && report.status === "completed");
    return matchesSearch && matchesSource && matchesStatus;
  });

  return (
    <div className="flex flex-1 h-[calc(100vh-56px)] w-full bg-gray-50/30 overflow-hidden">
      <LibrarySidebar
        collapsed={librarySidebarCollapsed}
        onToggle={() => setLibrarySidebarCollapsed(!librarySidebarCollapsed)}
      />
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-y-auto relative">

        {librarySidebarCollapsed && (
          <div className="flex items-center gap-3 px-6 pt-6 w-full">
            <button
              onClick={() => setLibrarySidebarCollapsed(false)}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-200 border border-gray-200 rounded-md transition-colors"
            >
              <PanelLeft size={18} />
            </button>
            <h2 className="text-xl font-semibold text-gray-900 m-0">My Library</h2>
          </div>
        )}

        <div
          className={cn(
            "flex-1 flex flex-col w-full px-4 sm:px-6 lg:px-8",
            librarySidebarCollapsed ? "pb-6 pt-4" : "py-6"
          )}
        >
          {/* Favorites Panel */}
          {isFavoriteTab && <FavoritesPanel activeTab={activeLibraryTab} />}

          {/* Saved Search Results Panel */}
          {isSavedSearchTab && <SavedSearchResultsPanel activeTab={activeLibraryTab} />}

          {/* Reports Panel & RA Reports Placeholder */}
          {isReportGroupTab && (
            <div className="flex flex-col">
              {/* Header & Filter Bar matching Favorites and Saved Searches */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200 mb-5">
                <div>
                  <h1 className="text-2xl font-bold text-explore tracking-tight m-0">Reports & Exports</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage and download your generated collaboration and benchmarking export files.
                  </p>
                </div>
              </div>

              {/* Sub-tabs header for Reports group */}
              <div className="border-b border-gray-200 mb-5">
                <nav className="flex space-x-6 -mb-px" aria-label="Reports Tabs">
                  {REPORT_TABS.map((t) => {
                    const isActive = currentReportTab === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setSearchParams({ tab: t.key })}
                        className={cn(
                          "pb-3 pt-1 px-1 border-b-2 text-sm transition-colors flex items-center gap-2 outline-none font-medium",
                          isActive
                            ? "border-explore text-explore font-semibold"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        )}
                      >
                        {t.label}
                        {t.badge && (
                          <span className="text-[10px] px-1.5 py-0.2 bg-violet-100 text-violet-700 font-medium rounded-full">
                            {t.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {isRAReportTab ? (
                <div className="flex flex-col items-center justify-center py-20 px-4 bg-white rounded-xl border border-dashed border-gray-300 text-center my-6">
                  <div className="w-16 h-16 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center mb-4 border border-violet-100 shadow-xs">
                    <Bot size={32} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">
                    RA Reports – Coming Soon
                  </h3>
                  <p className="text-sm text-gray-500 max-w-sm mb-4">
                    Automated research assistant reports, synthesized insights, and AI dossiers will appear here.
                  </p>
                  <Badge variant="secondary" className="text-xs bg-violet-50 text-violet-700 border border-violet-200">
                    Feature In Development
                  </Badge>
                </div>
              ) : (
                <>
                  <ExportToolbar
                    searchText={searchText}
                    onSearch={setSearchText}
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                    totalCount={filteredReports.length}
                    readyCount={filteredReports.filter(r => r.status === 'completed' || r.status === 'ready').length}
                    processingCount={filteredReports.filter(r => ['processing', 'pending', 'queued'].includes(r.status)).length}
                  />

                  <ExportList
                    reports={filteredReports}
                    loading={loading}
                    onDownload={handleDownload}
                    downloadingId={downloadingId}
                    onDelete={handleDelete}
                    onViewDetails={handleViewDetails}
                  />
                </>
              )}
            </div>
          )}
        </div>

        <ExportDetailDrawer
          visible={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          record={selectedRecord}
          onDownload={handleDownload}
          isDownloading={downloadingId === selectedRecord?.id}
        />
      </div>
    </div>
  );
};

export default MyLibraryPage;
